import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { CustomTooltip } from '../Tooltip';
import { CustomModal } from '../CustomModal';
import { ImageUpload } from '../ImageUpload';
import { CustomTagInput } from '../CustomTagInput';
import { deleteBookById, replaceBook } from '../../Store/api/books';
import { ModalPortal } from '../../components/ModalPortal';
import classIcon from '../../assets/images/classs-img.png';
import QuestionMark from '../../assets/images/question.svg';
import downArrow from '../../assets/images/chevron-down.svg';
import { BookOriginalDetails, BookRemixedDetails } from '../../Pages/BookOverview';
import { CustomSelect } from '../CustomSelect';
import { booksList } from '../../Store/api/classApi';

const radioOptions = [
  {
    label: 'Published',
    value: 'published',
    color: 'purple',
    content: 'Shared in the Greyled library for other professors to use in their classrooms.',
  },
  {
    label: 'Private',
    value: 'private',
    color: 'green',
    content: 'Can be used in your own classroom without sharing to the Greyled library.',
  },
  {
    label: 'Draft',
    value: 'draft',
    color: 'grey',
    content: 'Work in progress, only able to be seen by authors and collaborators.',
  },
];

export const modalVariations = (class_name = 'SPANISH101') => {
  return {
    draft: {
      //draft
      title: 'delete this book?',
      content:
        'Are you sure you want to delete this book? This can not be undone and all book information will be lost.',
      otherContent: (
        <>
          This is a <b className='text-uppercase text-light-grey'>draft</b> book and not associated
          with a class.
        </>
      ),
      actionBtnText: 'Delete book',
      cancelBtnText: 'Go back',
    },
    private: {
      // private
      title: 'delete this book?',
      content:
        'Are you sure you want to delete this book? This can not be undone and all book information will be lost.',
      otherContent: (
        <>
          This is a <b className='text-green text-uppercase'>private</b> book and not associated
          with a class.
        </>
      ),
      actionBtnText: 'Delete book',
      cancelBtnText: 'Go back',
    },
    private_upcoming: {
      title: 'delete this book?',
      content: `Are you sure you want to delete this book? All book content along with associated activities will be lost and can not be undone.`,
      otherContent: (
        <>
          This is a <b className='text-uppercase text-green'>private</b> book and is associated
          with the <b className='text-uppercase text-yellow'>upcoming</b> class{' '}
          <b className='text-uppercase text-black'>{class_name}.</b> You must change the book
          associated with this class before you are able to delete the book.
        </>
      ),
      highlightWord: 'private',
      highlight: 'private',
      actionBtnText: 'Delete book',
      cancelBtnText: 'Go back',
    },
    private_previous: {
      title: 'delete this book?',
      content:
        'Are you sure you want to delete this book? All book content along with associated activities will be lost and can not be undone.',
      otherContent: (
        <>
          This is a <b className='text-uppercase text-green'>private</b> book and is associated
          with the <b className='text-uppercase text-red'>previous</b> class{' '}
          <b className='text-uppercase text-black'>{class_name}.</b>
          <p className='m-0 mt-3'>
            This action can NOT be done because this class has already been completed and would
            affect all student activity grades.
          </p>
        </>
      ),
      actionBtnText: 'Delete book',
      cancelBtnText: 'Go back',
    },
    private_current: {
      //private_current
      title: 'delete this book?',
      content:
        'Are you sure you want to delete this book? All book content along with associated activities will be lost and can not be undone.',
      otherContent: (
        <>
          This is a <b className='text-uppercase text-green'>private</b> book and is associated
          with the <b className='text-uppercase text-red'>current</b> class{' '}
          <b className='text-uppercase text-black'>{class_name}.</b> You must change the book
          associated with this class before you are able to delete the book.
        </>
      ),
      actionBtnText: 'Delete book',
      cancelBtnText: 'Go back',
    },
    published: {
      title: 'delete this book?',
      content:
        'Are you sure you want to delete this book? All book content along with associated activities will be lost and can not be undone.',
      otherContent: (
        <>
          This is a <b className='text-uppercase text-purple'>published</b> book and not associated
          with a class.
          <p className=' pt-2 mb-0'>
            Deleting this book will only remove your version of the book. It will not remove any
            versions used by other professors in Greyled but you will still be attributed
            accordingly.
          </p>
        </>
      ),
      actionBtnText: 'Delete book',
      cancelBtnText: 'Go back',
    },
    published_previous: {
      title: 'delete this book?',
      content:
        'Are you sure you want to delete this book? All book content along with associated activities will be lost and can not be undone.',
      otherContent: (
        <>
          This is a <b className='text-uppercase text-purple'>published</b> book and is associated
          with the <b className='text-uppercase text-red'>previous</b> class{' '}
          <b className='text-uppercase text-black'>{class_name}.</b>
          <p className='m-0 mt-3'>
            This action can NOT be done because this class has already been completed and would
            affect all student activity grades.
          </p>
        </>
      ),
      actionBtnText: 'Delete book',
      cancelBtnText: 'Go back',
    },
    published_upcoming: {
      //published_upcoming
      title: 'delete this book?',
      content:
        'Are you sure you want to delete this book? All book content along with associated activities will be lost and can not be undone.',
      otherContent: (
        <>
          This is a <b className='text-uppercase text-purple'>published</b> book and is associated
          with the <b className='text-uppercase text-yellow'>upcoming</b> class{' '}
          <b>{class_name}.</b> Make sure to update the class book.
          <p className=' pt-2 mb-0'>
            Deleting this book will only remove your version of the book. It will not remove any
            versions used by other professors in Greyled but you will still be attributed
            accordingly.
          </p>
        </>
      ),
      actionBtnText: 'Delete book',
      cancelBtnText: 'Go back',
    },
    published_current: {
      title: 'delete this book?',
      content:
        'Are you sure you want to delete this book? All book content along with associated activities will be lost and can not be undone.',
      otherContent: (
        <>
          This is a <b className='text-uppercase text-purple'>published</b> book and is associated
          with the <b className='text-uppercase text-red'>current</b> class{' '}
          <b className='text-uppercase text-black'>{class_name}.</b> You must change the book
          associated with this class before you are able to delete the book.
        </>
      ),
      actionBtnText: 'Delete book',
      cancelBtnText: 'Go back',
    },
  };
};

export const getBookStatus = (bookObj) => {
  if (bookObj.bookStatus === 'draft') {
    return 'draft';
  } else if (bookObj.bookStatus === 'private' && !bookObj?.classesArray?.length) {
    return 'private';
  } else if (
    bookObj.bookStatus === 'private' &&
    bookObj?.classesArray?.length &&
    bookObj.classesArray.filter((item) => item.classStatus === 'CURRENT').length
  ) {
    return 'private_current';
  } else if (
    bookObj.bookStatus === 'private' &&
    bookObj?.classesArray?.length &&
    bookObj.classesArray.filter((item) => item.classStatus === 'PREVIOUS').length
  ) {
    return 'private_previous';
  } else if (
    bookObj.bookStatus === 'private' &&
    bookObj?.classesArray?.length &&
    bookObj.classesArray.filter((item) => item.classStatus === 'UPCOMING').length
  ) {
    return 'private_upcoming';
  } else if (bookObj.bookStatus === 'published' && !bookObj?.classesArray?.length) {
    return 'published';
  } else if (
    bookObj.bookStatus === 'published' &&
    bookObj?.classesArray?.length &&
    bookObj.classesArray.filter((item) => item.classStatus === 'CURRENT').length
  ) {
    return 'published_current';
  } else if (
    bookObj.bookStatus === 'published' &&
    bookObj?.classesArray?.length &&
    bookObj.classesArray.filter((item) => item.classStatus === 'PREVIOUS').length
  ) {
    return 'published_previous';
  } else if (
    bookObj.bookStatus === 'published' &&
    bookObj?.classesArray?.length &&
    bookObj.classesArray.filter((item) => item.classStatus === 'UPCOMING').length
  ) {
    return 'published_upcoming';
  } else return 'draft';
  //  rest of status need to manage with API updates
};

export const classesStatus = (classesArray = []) => ({
  private_previous: classesArray.find((item) => item.classStatus === 'PREVIOUS'),
  private_current: classesArray.find((item) => item.classStatus === 'CURRENT'),
  private_upcoming: classesArray.find((item) => item.classStatus === 'UPCOMING'),
  published_previous: classesArray.find((item) => item.classStatus === 'PREVIOUS'),
  published_current: classesArray.find((item) => item.classStatus === 'CURRENT'),
  published_upcoming: classesArray.find((item) => item.classStatus === 'UPCOMING'),
});

const indicatorSeparatorStyle = {
  alignSelf: 'stretch',
  marginBottom: 8,
  marginTop: 8,
  width: 0,
};

const IndicatorSeparator = ({ innerProps }) => {
  return <span style={indicatorSeparatorStyle} {...innerProps} />;
};

export const Overview = ({ loader, bookObj, handleBookUpdate, handleStatusUpdate }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const licenseRef = useRef(null);
  const languageRef = useRef(null);
  const categoryRef = useRef(null);
  const [url, setUrl] = useState('');
  const materialTypeRef = useRef(null);
  const [book, setBook] = useState({});
  const [title, setTitle] = useState('');
  const [edit, setEdit] = useState(false);
  const [status, setStatus] = useState('');
  const [subjects, setSubjects] = useState([]);
  const [selected, setSelected] = useState('');
  const [license, setLicense] = useState(false);
  const [loading, setLoading] = useState(false);
  const [bookList, setBooksList] = useState([]); // useState for replaced books listing API
  const [language, setLanguage] = useState(false);
  const [category, setCategory] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [materialType, setMaterialType] = useState(false);
  const [currentSelectedBook, setCurrentSelectedBook] = useState({});
  const [showPublishModal, setShowPublishModal] = useState(false);
  const { categories, materialTypes, languages, licenses } = useSelector(
    (state) => state.entities.bookFelids.bookFields
  );

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    bookObj.bookStatus && setSelected(bookObj.bookStatus);
    setBook(bookObj);
    bookObj?.subjects?.length && setSubjects(bookObj.subjects);
  }, [bookObj, bookObj.bookStatus]);

  useEffect(() => {
    dispatch(
      booksList({
        onSuccess: (res) => {
          const { data = [] } = res;
          setBooksList(data.filter((item) => item._id !== (bookObj._id || bookObj.bookId)));
        },
      })
    );
  }, []);

  const handleClickOutside = (event) => {
    if (languageRef.current && !languageRef.current.contains(event.target)) {
      setLanguage(false);
    }
    if (materialTypeRef.current && !materialTypeRef.current.contains(event.target)) {
      setMaterialType(false);
    }
    if (categoryRef.current && !categoryRef.current.contains(event.target)) {
      setCategory(false);
    }
    if (licenseRef.current && !licenseRef.current.contains(event.target)) {
      setLicense(false);
    }
  };

  const handleStatusChange = (item) => {
    if (item === 'published') {
      setStatus(item);
      setShowPublishModal(true);
    } else {
      handleStatusUpdate(item);
      setSelected(item);
    }
  };

  const handleImageUpdate = (imgUrl) => setUrl(imgUrl);
  const MaterialTypeToogle = () => setMaterialType(!materialType);

  const handleAddTag = (inputValue) => {
    if (inputValue.trim() !== '' && subjects.length <= 3) {
      setSubjects([...subjects, inputValue.trim()]);
    }
  };
  const setBookObjField = (value, name) => {
    setBook((old) => ({ ...old, [name]: value }));
    setMaterialType(false);
    setLanguage(false);
    setLicense(false);
    setCategory(false);
  };

  const onHide = useCallback(() => {
    setTitle('');
    setLoading(false);
    setShowModal(false);
    setShowPublishModal(false);
    setCurrentSelectedBook({});
  }, []);

  const deleteBook = () => {
    setLoading(true);
    if (currentSelectedBook?.label && currentSelectedBook?.value) {
      dispatch(
        replaceBook({
          oldBookId: book?.bookId || book?._id,
          newBookId: currentSelectedBook.value,
          onSuccess: () => {
            toast.success('Book deleted successfully.');
            navigate(`/dashboard/bookshelf`);
          },
          onError: () => {
            toast.error('Error while deleting book.');
          },
          onEnd: () => {
            onHide();
          },
        })
      );
    } else
      dispatch(
        deleteBookById({
          bookId: book?.bookId || book?._id,
          status: book.bookStatus,
          onSuccess: () => {
            toast.success('Book deleted successfully.');
            navigate(`/dashboard/bookshelf`);
          },
          onError: () => {
            toast.error('Error while deleting book.');
          },
          onEnd: () => {
            onHide();
          },
        })
      );
  };
  const removeTag = (tag) => {
    const list = [...subjects];
    const filtered = list.filter((tg) => tg !== tag);
    setSubjects(filtered);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    handleBookUpdate({
      subjects,
      isbn: book.isbn,
      title: book.title,
      license: book.license,
      language: book.language,
      description: book.description,
      categoryType: book.categoryType,
      materialType: book.materialType,
      publisher: book.publisher,
      coverImage: url || book.coverImage,
      yearPublished: new Date(book.yearPublished),
    });
    setTimeout(() => {
      setEdit(false);
    }, 2000);
  };

  const onAction = () => {
    handleStatusUpdate(status);
    setSelected(status);
    onHide();
  };

  const book_status = getBookStatus(bookObj);
  const classDetails = classesStatus(bookObj.classesArray)[book_status];

  return (
    <>
      <ModalPortal>
        <CustomModal
          is_delete
          onHide={onHide}
          loading={loading}
          showModal={showModal}
          onAction={deleteBook}
          title_matched={
            ['private_upcoming', 'published_upcoming'].includes(book_status)
              ? !(book.title === title && currentSelectedBook?.label)
              : !(book.title === title)
          }
          // pass additional parameter for action button style. based on book title and title enter by user.
          {...modalVariations(classDetails?.courseName || '')?.[book_status]}
        >
          {['private_upcoming', 'published_upcoming'].includes(book_status) && (
            <div className='d-flex flex-column'>
              <p className='mb-0 mt-2'>
                Choose a book replacement for all associated classes. Any assigned activities will
                need to be reassigned.
              </p>
              <div className='form-group mb-0 w-100'>
                <CustomSelect
                  customStyle={{
                    control: (base, state) => ({
                      ...base,
                      border: 'none !important',
                      background: '#fafaf9',
                      boxShadow: 'none',
                      borderBottom: state.isFocused
                        ? '1px solid #2e2e2d !important'
                        : '1px solid #2e2e2d !important',
                      '&:hover': {
                        borderBottom: '1px solid #2e2e2d', // Keep border on hover
                      },
                    }),
                    menu: (provided) => ({
                      ...provided,
                      marginTop: 0, // This removes the gap between control and dropdown
                      border: '1px solid grey',
                    }),
                  }}
                  isClearable={false}
                  className='w-100 mb-0'
                  placeholder='Select book'
                  value={bookList
                    .filter((item) => item._id === currentSelectedBook.value)
                    .map((item) => ({ value: item._id, label: item.title }))}
                  options={bookList.map((item) => ({ value: item._id, label: item.title }))}
                  handleChange={(value) => {
                    setCurrentSelectedBook(value);
                  }}
                  components={{
                    IndicatorSeparator,
                  }}
                />
              </div>
            </div>
          )}
          {![
            'private_previous',
            'private_current',
            'published_current',
            'published_previous',
          ].includes(book_status) && (
            <div className='d-flex flex-column'>
              <p className='mb-0'>
                Please type the book title <span className='fw-500'>“{bookObj.title}”</span> below
                to confirm deletion.
              </p>
              <div className='form-group mb-0'>
                <input
                  type='text'
                  name='title'
                  value={title}
                  placeholder='Title of the textbook'
                  onChange={(e) => setTitle(e.target.value)}
                  className='form-control pl_0 book_detail_overview_title mb-0'
                />
              </div>
            </div>
          )}
        </CustomModal>
        <CustomModal
          onHide={onHide}
          loading={loading}
          onAction={onAction}
          showModal={showPublishModal}
          title='publish your book!'
          cancelBtnText='Back to book overview'
          actionBtnText='Publish book to Greyled'
          otherContent='This action can NOT be undone or go back to a draft state. Make sure the book and activities are ready to be shared with other professors and to use in classrooms.'
          content='Are you ready to publish your book to the Greyled library where it can be discovered by other professors? Your book and associated activities can be shared and used in any classroom.'
        />
      </ModalPortal>
      <div className='d-flex flex-column gap-2'>
        {radioOptions.map((item) => (
          <div key={item.value} className='d-flex align-content-center gap-4'>
            <div className='iagree_radio radio_list align-content-center w-7'>
              <input
                type='radio'
                name={`${item.value}`}
                id={`option_${item.value}`}
                checked={item.value === selected}
                onChange={() => handleStatusChange(item.value)}
              />
              <label
                htmlFor={`option_${item.value}`}
                className={`drop_down_list text-uppercase fw-bold my-1 text-${item.color}`}
              >
                {item.label}
              </label>
            </div>
            <span>{item.content}</span>
          </div>
        ))}
      </div>
      {edit ? (
        <form onSubmit={handleSubmit}>
          <div className='d-flex flex-column gap-2'>
            <div className='d-flex flex-column gap-2'>
              <div className='form-group mb-0'>
                <input
                  type='text'
                  name='title'
                  className='form-control pl_0 book_detail_overview_title mb-0 black_divider_line_width'
                  placeholder='Title of the textbook'
                  value={book?.title}
                  onChange={(e) => setBookObjField(e.target.value, e.target.name)}
                  // ref={(el) => (el ? autoResize({target: el}) : null)} // Automatically adjust the height on mount
                />
                {/* {errors.title && <span className="error-create"> */}
                {/* {errors.title} */}
                {/* </span>} */}
              </div>
            </div>
            <div className='d-flex flex-row gap-5 mt-3'>
              <ImageUpload
                url={book.coverImage || bookObj?.bookDetailCoverUrl || url}
                maxImgWidth={177}
                maxImgHeight={216}
                handleImageUpdate={handleImageUpdate}
              />
              <div className='d-flex flex-wrap column-gap-50 row-gap-20 w-100'>
                <div className='form-group mb-0 w-32'>
                  <div className='select_wrap_create' ref={materialTypeRef}>
                    <p
                      className={`cursor_pointer m-0 form-control pl_0 custom_select_create text-grey-create ${
                        materialType ? 'open' : ''
                      }`}
                      onClick={MaterialTypeToogle}
                    >
                      {book.materialType || 'Material type'}
                    </p>
                    {materialType && (
                      <div className='drop_down_list_create' style={{ display: 'block' }}>
                        <ul>
                          {materialTypes.map((material, index) => (
                            <li
                              key={index}
                              style={{
                                display: `${book.materialType === material ? 'none' : ''}`,
                              }}
                              onClick={() => {
                                setBookObjField(material, 'materialType');
                              }}
                            >
                              {material}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                  {/* <span className="error-create" style={{ display: "none" }}>
                    please select title
                  </span> */}
                </div>

                <div className='form-group mb-0 w-32'>
                  <CustomTagInput
                    tags={subjects}
                    handleAddTag={handleAddTag}
                    removeTag={removeTag}
                  />
                </div>

                <div className='form-group mb-0 w-32'>
                  <div className='select_wrap_create' ref={categoryRef}>
                    <div
                      className={`cursor_pointer m-0 form-control pl_0 custom_select_create text-grey-create d-flex justify-content-between mb-0 ${
                        category ? 'open' : ''
                      }`}
                      onClick={() => {
                        setCategory((old) => !old);
                      }}
                    >
                      {book.categoryType || 'Category'}
                      {!category && (
                        <img
                          src={downArrow}
                          alt='ico here'
                          style={{ marginRight: '10px', transform: 'rotate(180deg)' }}
                        />
                      )}
                    </div>
                    {category && (
                      <div
                        className='drop_down_list_create'
                        style={{
                          display: 'block',
                          bottom: '50px',
                          borderBottomLeftRadius: '0px',
                          borderBottomRightRadius: '0px',
                          maxHeight: '15rem',
                        }}
                      >
                        <ul>
                          {categories.map((category, index) => (
                            <li
                              key={index}
                              style={{
                                display: `${book.categoryType === category ? 'none' : ''}`,
                              }}
                              onClick={() => {
                                setBookObjField(category, 'categoryType');
                              }}
                            >
                              {category}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                  {/* <span className="error-create" style={{ display: "none" }}>
                    please select title
                  </span> */}
                </div>

                <div className='form-group position-relative mb-0 w-32'>
                  <input
                    type='text'
                    className='form-control pl_0'
                    placeholder={'Year published'}
                    value={book.yearPublished}
                    name='yearPublished'
                    onChange={(e) => {
                      setBookObjField(e.target.value, e.target.name);
                    }}
                  />
                  <CustomTooltip position='top' content='Enter the year the book was published.'>
                    <span className='position-absolute top-40 end-0 translate-middle-y me-0'>
                      <img src={QuestionMark} alt='icon' className='svg-icon' />
                    </span>
                  </CustomTooltip>
                </div>

                <div className='form-group mb-0 w-32'>
                  <div className='select_wrap_create' ref={licenseRef}>
                    {license && (
                      <div
                        className='drop_down_list_create'
                        style={{
                          display: 'block',
                          bottom: '50px',
                          borderBottomLeftRadius: '0px',
                          borderBottomRightRadius: '0px',
                          maxHeight: '15rem',
                        }}
                      >
                        <ul>
                          {licenses.map((license, index) => (
                            <li
                              className={`${
                                book.license === license.label ? 'active_option_state' : ''
                              }`}
                              key={index}
                              onClick={() => {
                                setBookObjField(license.label, 'license');
                                setLicense(false);
                              }}
                            >
                              <div className='d-flex justify-content-between align-items-center'>
                                <div className='d-flex' style={{ gap: '14px' }}>
                                  <span>{license.label}</span>
                                  <div className='d-flex' style={{ gap: '8px' }}>
                                    {license.img_url && (
                                      <img
                                        src={license.img_url}
                                        width={22}
                                        height={22}
                                        alt='license icons here'
                                      />
                                    )}
                                    {license.img_url2 && (
                                      <img
                                        src={license.img_url2}
                                        width={22}
                                        height={22}
                                        alt='license icons here'
                                      />
                                    )}
                                    {license.img_url3 && (
                                      <img
                                        src={license.img_url3}
                                        width={22}
                                        height={22}
                                        alt='license icons here'
                                      />
                                    )}
                                  </div>
                                </div>
                                <a
                                  href={license.linkTo}
                                  target='_blank'
                                  rel='noreferrer'
                                  referrerPolicy='no-referrer'
                                >
                                  <img
                                    src='/assets/images/external_link.svg'
                                    width={22}
                                    height={22}
                                    alt='external_link'
                                  />
                                </a>
                              </div>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                    <div
                      className={`cursor_pointer m-0 form-control pl_0 custom_select_create text-grey-create d-flex justify-content-between mb-0 ${
                        license ? 'open' : ''
                      }`}
                      onClick={() => {
                        setLicense((old) => !old);
                      }}
                    >
                      {book.license || 'License'}
                      {!license && (
                        <img
                          src={downArrow}
                          alt='ico here'
                          style={{ marginRight: '10px', transform: 'rotate(180deg)' }}
                        />
                      )}
                    </div>
                  </div>
                </div>

                <div className='form-group position-relative mb-0 w-32'>
                  <input
                    type='text'
                    className='form-control pl_0'
                    placeholder={'Published'}
                    value={book.publisher}
                    name='publisher'
                    onChange={(e) => {
                      setBookObjField(e.target.value, e.target.name);
                    }}
                  />
                  <CustomTooltip
                    position='top'
                    content='Enter the location or website that it was published by.'
                  >
                    <span className='position-absolute top-40 end-0 translate-middle-y me-0'>
                      <img src={QuestionMark} alt='icon' className='svg-icon' />
                    </span>
                  </CustomTooltip>
                </div>

                <div className='form-group mb-0 w-32'>
                  <div className='select_wrap_create' ref={languageRef}>
                    <div
                      className={`cursor_pointer m-0 form-control pl_0 custom_select_create text-grey-create mb-0 d-flex justify-content-between ${
                        language ? 'open' : ''
                      }`}
                      onClick={() => {
                        setLanguage((old) => !old);
                      }}
                    >
                      {book.language || 'Language'}
                      {!language && (
                        <img
                          src={downArrow}
                          alt='ico here'
                          style={{ marginRight: '10px', transform: 'rotate(180deg)' }}
                        />
                      )}
                    </div>
                    {language && (
                      <div
                        className='drop_down_list_create'
                        style={{
                          display: 'block',
                          bottom: '51px',
                          borderBottomLeftRadius: '0px',
                          borderBottomRightRadius: '0px',
                        }}
                      >
                        <ul>
                          {languages.map((language, index) => (
                            <li
                              key={index}
                              style={{
                                display: `${book.language === language ? 'none' : ''}`,
                              }}
                              onClick={() => {
                                setBookObjField(language, 'language');
                              }}
                            >
                              {language}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                  {/* <span className="error-create" style={{ display: "none" }}>
                    please select title
                  </span> */}
                </div>

                <div className='form-group position-relative mb-0 w-32'>
                  <input
                    type='text'
                    className='form-control pl_0'
                    placeholder={'ISBN'}
                    value={book.isbn}
                    name='isbn'
                    onChange={(e) => {
                      setBookObjField(e.target.value, e.target.name);
                    }}
                  />
                  <CustomTooltip
                    position='top'
                    content='If your book does not have ISBN, then leave it blank. One will be provided to you by Greyled.'
                  >
                    <span className='position-absolute top-40 end-0 translate-middle-y me-0'>
                      <img src={QuestionMark} alt='icon' className='svg-icon' />
                    </span>
                  </CustomTooltip>
                </div>
              </div>
            </div>
            <div className='d-flex flex-column gap-2'>
              <div className='form-group mb-0'>
                <input
                  type='text'
                  name='description'
                  className='form-control pl_0 book_detail_overview_title mb-0 black_divider_line_width'
                  placeholder='Description'
                  value={book.description}
                  onChange={(e) => {
                    setBookObjField(e.target.value, e.target.name);
                  }}
                />
                {/* {errors.title && <span className="error-create"> */}
                {/* {errors.title} */}
                {/* </span>} */}
              </div>
            </div>
            <div className='d-flex justify-content-between w-73 my-3'>
              <span className='cancel-btn cursor mb-2' onClick={() => setEdit(false)}>
                Cancel
              </span>
              <button className='cursor save-btn rounded-0' type='submit'>
                Save --&gt;
              </button>
            </div>
          </div>
        </form>
      ) : (
        <>
          <div className='d-flex gap-60'>
            <div className='bookview-img-container'>
              {loader ? (
                <h4 className='content-skeleton m-0'></h4>
              ) : (
                <img
                  src={bookObj.coverImage || bookObj?.bookDetailCoverUrl || classIcon}
                  width={244}
                  height={329}
                  alt='class icon here'
                />
              )}
            </div>
            <div className='d-flex flex-column gap-60 w-52'>
              <div className='bookview-content-container d-flex flex-column w-100'>
                <div className='bookview-tag-container d-flex justify-content-between align-items-center mb-2'>
                  {loader ? (
                    <h4 className='content-skeleton m-0 w-100'></h4>
                  ) : (
                    <div className='breadcrumb fw-semibold mb-0'>
                      {`${bookObj.categoryType || 'Category'} / ${
                        bookObj?.subjects?.join(', ') || 'Subjects'
                      }`}
                    </div>
                  )}
                  {!loader && (
                    <button
                      className='book_detail_edit_btn book_shelft_detail_action_button_text bg-white fw_400 mb-0'
                      onClick={() => setEdit(true)}
                    >
                      Edit Details
                    </button>
                  )}
                </div>
                <BookOriginalDetails details={bookObj} loading={loader} />
              </div>
              <div>
                <BookRemixedDetails loading={loader} bookObj={bookObj} />
              </div>
            </div>
          </div>
          {!loader && (
            <div className='d-flex align-items-center justify-content-between w-73 text-uppercase fw-semibold pt-2 mb-4'>
              <span>DELETE THIS BOOK?</span>
              <button className='delete-btn' onClick={() => setShowModal(true)}>
                Delete
              </button>
            </div>
          )}
        </>
      )}
    </>
  );
};
